import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, TrendingUp, LineChart, CalendarRange, DollarSign, ArrowUpCircle, ArrowDownCircle, Wallet, Calendar, ChevronRight, BanknoteIcon, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Booking } from '../lib/bookings';
import { Payment } from '../lib/payments';
import {
  fetchStandaloneInvoiceSales,
  sumStandaloneInvoiceSales,
  type StandaloneInvoiceSale,
} from '../lib/invoices/standaloneInvoiceSales';
import PortalDropdown from '../components/ui/PortalDropdown';

const dateRanges = [
  { id: 'this-month', name: 'This Month' },
  { id: 'last-month', name: 'Last Month' },
  { id: 'next-month', name: 'Next Month' },
  { id: 'custom', name: 'Custom Range', icon: CalendarRange }
];

// Mock data for metrics
const mockMetrics = [
  {
    id: 'total-revenue',
    name: 'Total Sales After Discounts',
    value: '$0.00',
    change: '+0.0%',
    trend: 'up',
    icon: DollarSign
  },
  {
    id: 'total-costs',
    name: 'Service Costs',
    value: '$0.00',
    change: '+0.0%',
    trend: 'up',
    icon: Wallet
  },
  {
    id: 'net-profit',
    name: 'Gross Profit',
    value: '$0.00',
    change: '+0.0%',
    trend: 'up',
    icon: TrendingUp
  }
];

interface MaterialData {
  id: string;
  name: string;
  category: string;
  quantity: number;
  unit: string;
  cost_per_unit: number;
  total_cost: number;
  material_date: string;
}

interface LaborData {
  id: string;
  resource_name: string;
  hours: number;
  rate: number;
  total_cost: number;
  booking_id: string;
}

interface CategoryTotal {
  category: string;
  total_cost: number;
  materials: MaterialData[];
  isExpanded: boolean;
}

interface ResourceTotal {
  resource_id: string;
  first_name: string;
  last_name: string;
  total_earnings: number;
}

interface Business {
  id: string;
  name: string;
}

interface ProfitByJobBooking {
  id: string;
  booking_number: string;
  title: string;
  subtotal: number;
  discount_amount: number;
  material_costs: number;
  labour_costs: number;
  completed_at: string;
  status: string;
}

const reportPages = [
  { id: 'sales', name: 'Sales', icon: TrendingUp, path: '/sales-reports/sales' },
  { id: 'profit-loss', name: 'Profit & Loss', icon: LineChart, path: '/sales-reports/profit-loss' }
];

const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', { 
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

const getDateRangeText = (rangeId: string, startDate?: string, endDate?: string): string => {
  // Helper to create a UTC date that represents local midnight
  const createLocalDate = (date: Date | string) => {
    if (typeof date === 'string') {
      // For custom range dates, which come in as YYYY-MM-DD
      return new Date(date + 'T00:00:00Z');
    }
    // For other dates, ensure we're working with UTC midnight
    return new Date(Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    ));
  };

  const now = new Date();
  const utcNow = createLocalDate(now);

  switch (rangeId) {
    case 'this-month': {
      const monthName = now.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
      return monthName;
    }
    case 'last-month': {
      const lastMonth = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() - 1, 1));
      return lastMonth.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
    }
    case 'next-month': {
      const nextMonth = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 1, 1));
      return nextMonth.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
    }
    case 'custom': {
      if (startDate && endDate) {
        const start = createLocalDate(startDate);
        const end = createLocalDate(endDate);
        return `${formatDate(start)} - ${formatDate(end)}`;
      }
      return 'Custom Range';
    }
    default:
      return 'Select Date Range';
  }
};

// Function to check if a date falls within the selected range
const isDateInRange = (date: string | null | undefined, rangeId: string, customStartDate?: string, customEndDate?: string): boolean => {
  if (!date) return false;
  
  // Convert the booking end date to UTC midnight
  const targetDate = new Date(date + 'T00:00:00Z');
  const now = new Date();
  const utcNow = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));

  switch (rangeId) {
    case 'this-month': {
      const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), 1));
      const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 1, 0, 23, 59, 59, 999));
      return targetDate >= start && targetDate <= end;
    }
    case 'last-month': {
      const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() - 1, 1));
      const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), 0, 23, 59, 59, 999));
      return targetDate >= start && targetDate <= end;
    }
    case 'next-month': {
      const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 1, 1));
      const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 2, 0, 23, 59, 59, 999));
      return targetDate >= start && targetDate <= end;
    }
    case 'custom': {
      if (customStartDate && customEndDate) {
        // Convert custom dates to UTC midnight
        const start = new Date(customStartDate + 'T00:00:00Z');
        const end = new Date(customEndDate + 'T23:59:59.999Z');
        return targetDate >= start && targetDate <= end;
      }
      return false;
    }
    default:
      return false;
  }
};

interface Resource {
  id: string;
  first_name: string;
  last_name: string;
}

interface PayrollEntry {
  id: string;
  resource_id: string;
  total_earnings: number;
  completed_at?: string;
  clock_out?: string;
  resource: {
    first_name: string;
    last_name: string;
  };
}

interface PayrollResponse {
  id: string;
  resource_id: string;
  total_earnings: number;
  completed_at?: string;
  clock_out?: string;
  resource: {
    first_name: string;
    last_name: string;
  };
}

export default function ProfitLoss() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState(dateRanges[0]);
  const [isCustomRangeOpen, setIsCustomRangeOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [materialUsageData, setMaterialUsageData] = useState<CategoryTotal[]>([]);
  const [laborData, setLaborData] = useState<ResourceTotal[]>([]);
  const [isLoadingMaterials, setIsLoadingMaterials] = useState(true);
  const [isLoadingLabor, setIsLoadingLabor] = useState(true);
  const [salesData, setSalesData] = useState({
    totalSales: 0,
    salesChange: 0,
    totalAfterDiscounts: 0,
    afterDiscountsChange: 0,
    totalOrders: 0,
    orderChange: 0,
    bookings: [] as Booking[]
  });
  const [profitByJobData, setProfitByJobData] = useState<ProfitByJobBooking[]>([]);
  const [paymentData, setPaymentData] = useState({
    totalPaymentsIn: 0,
    totalPaymentsOut: 0,
    paymentsInChange: 0,
    paymentsOutChange: 0
  });
  const [stockData, setStockData] = useState({
    totalSuppliesCost: 0,
    suppliesChange: 0
  });
  const [fixedExpenses, setFixedExpenses] = useState({
    totalFixedCost: 0,
    fixedCostChange: 0
  });
  const [useStockHistory, setUseStockHistory] = useState(() => {
    // Initialize from localStorage, default to false if not set
    const saved = localStorage.getItem('useStockHistory');
    return saved ? JSON.parse(saved) : false;
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();

  // Get current page name based on path
  const getCurrentPageName = () => {
    const currentPage = reportPages.find(page => page.path === location.pathname) || reportPages[0];
    return currentPage.name;
  };

  const handlePageSelect = (path: string) => {
    setIsDropdownOpen(false);
    navigate(path);
  };

  const handleDateSelect = (dateRange: typeof dateRanges[0]) => {
    setSelectedDateRange(dateRange);
    if (dateRange.id === 'custom') {
      setIsCustomRangeOpen(true);
    } else {
      setIsCustomRangeOpen(false);
      setIsDateDropdownOpen(false);
    }
  };

  const handleCustomRangeSubmit = () => {
    setIsDateDropdownOpen(false);
    setIsCustomRangeOpen(false);
  };

  // Date range utility functions
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', { 
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC'
    });
  };

  const getDateRangeText = (rangeId: string, startDate?: string, endDate?: string): string => {
    const now = new Date();
    switch (rangeId) {
      case 'this-month':
        return now.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
      case 'last-month':
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        return lastMonth.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
      case 'next-month':
        const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        return nextMonth.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
      case 'custom':
        if (startDate && endDate) {
          const start = new Date(startDate + 'T00:00:00Z');
          const end = new Date(endDate + 'T00:00:00Z');
          return `${formatDate(start)} - ${formatDate(end)}`;
        }
        return 'Custom Range';
      default:
        return 'Select Date Range';
    }
  };

  const isDateInRange = (date: string | null | undefined, rangeId: string, customStartDate?: string, customEndDate?: string): boolean => {
    if (!date) return false;
    
    const targetDate = new Date(date + 'T00:00:00Z');
    const now = new Date();
    const utcNow = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));

    switch (rangeId) {
      case 'this-month': {
        const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), 1));
        const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 1, 0, 23, 59, 59, 999));
        return targetDate >= start && targetDate <= end;
      }
      case 'last-month': {
        const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() - 1, 1));
        const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), 0, 23, 59, 59, 999));
        return targetDate >= start && targetDate <= end;
      }
      case 'next-month': {
        const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 1, 1));
        const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 2, 0, 23, 59, 59, 999));
        return targetDate >= start && targetDate <= end;
      }
      case 'custom': {
        if (customStartDate && customEndDate) {
          const start = new Date(customStartDate + 'T00:00:00Z');
          const end = new Date(customEndDate + 'T23:59:59.999Z');
          return targetDate >= start && targetDate <= end;
        }
        return false;
      }
      default:
        return false;
    }
  };

  useEffect(() => {
    fetchMaterialUsage();
    fetchLaborData();
  }, [selectedBusiness?.id, selectedDateRange, customStartDate, customEndDate]);

  const fetchMaterialUsage = async () => {
    if (!supabase || !selectedBusiness?.id) return;
    
    setIsLoadingMaterials(true);
    try {
      // Get date range based on selected filter
      let startDate: string | null = null;
      let endDate: string | null = null;

      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      switch (selectedDateRange.id) {
        case 'this-month':
          startDate = firstDayOfMonth.toISOString().split('T')[0];
          endDate = lastDayOfMonth.toISOString().split('T')[0];
          break;
        case 'last-month':
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
          endDate = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
          break;
        case 'next-month':
          startDate = new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString().split('T')[0];
          endDate = new Date(now.getFullYear(), now.getMonth() + 2, 0).toISOString().split('T')[0];
          break;
        case 'custom':
          startDate = customStartDate;
          endDate = customEndDate;
          break;
      }

      console.log('Fetching materials with date range:', { startDate, endDate });

      const { data: rawData, error: materialsError } = await supabase
        .from('booking_materials')
        .select(`
          id,
          quantity,
          cost,
          total_cost,
          created_at,
          material:material_id (
            id,
            name,
            description,
            unit,
            category:category_id (
              id,
              name
            )
          ),
          booking:booking_id (
            id,
            status,
            end_date,
            business_id
          )
        `)
        .eq('booking.business_id', selectedBusiness.id)
        .eq('booking.status', 'completed')
        .gte('booking.end_date', startDate)
        .lte('booking.end_date', endDate);

      if (materialsError) {
        console.error('Error fetching materials:', materialsError);
        throw materialsError;
      }

      console.log('Raw materials data:', rawData);

      if (!rawData?.length) {
        console.log('No materials data found');
        setMaterialUsageData([]);
        return;
      }

      // Process and group the data by category
      const groupedData = rawData.reduce((acc: { [key: string]: { [key: string]: MaterialData } }, item: any) => {
        // Only include materials from completed bookings with end_date in range
        if (item.booking?.status !== 'completed' || 
            !item.booking?.end_date || 
            !isDateInRange(item.booking.end_date, selectedDateRange.id, customStartDate, customEndDate)) {
          return acc;
        }
        
        const category = item.material?.category?.name || 'Uncategorized';
        const materialId = item.material?.id || 'unknown';
        
        if (!acc[category]) {
          acc[category] = {};
        }
        
        if (!acc[category][materialId]) {
          acc[category][materialId] = {
            id: materialId,
            name: item.material?.name || 'Unknown Material',
            category,
            quantity: 0,
            unit: item.material?.unit || 'unit',
            cost_per_unit: item.cost,
            total_cost: 0,
            material_date: item.booking.end_date
          };
        }

        // Sum up quantities and costs
        acc[category][materialId].quantity += item.quantity;
        acc[category][materialId].total_cost += item.total_cost;
        
        return acc;
      }, {});

      // Convert to array format with totals
      const categoryTotals: CategoryTotal[] = Object.entries(groupedData).map(([category, materials]) => ({
        category,
        total_cost: Object.values(materials).reduce((sum, mat) => sum + mat.total_cost, 0),
        materials: Object.values(materials).sort((a, b) => b.total_cost - a.total_cost),
        isExpanded: false,
      }));

      // Sort by total cost descending
      categoryTotals.sort((a, b) => b.total_cost - a.total_cost);
      
      console.log('Processed material data:', categoryTotals);
      setMaterialUsageData(categoryTotals);
    } catch (error) {
      console.error('Error fetching material usage:', error);
      setMaterialUsageData([]);
    } finally {
      setIsLoadingMaterials(false);
    }
  };

  const fetchLaborData = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      setIsLoadingLabor(true);
      
      // Get date range
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      let start: Date;
      let end: Date;

      switch (selectedDateRange.id) {
        case 'this-month':
          start = firstDayOfMonth;
          end = lastDayOfMonth;
          break;
        case 'last-month':
          start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          end = new Date(now.getFullYear(), now.getMonth(), 0);
          break;
        case 'next-month':
          start = new Date(now.getFullYear(), now.getMonth() + 1, 1);
          end = new Date(now.getFullYear(), now.getMonth() + 2, 0);
          break;
        case 'custom':
          start = customStartDate ? new Date(customStartDate) : firstDayOfMonth;
          end = customEndDate ? new Date(customEndDate) : lastDayOfMonth;
          break;
        default:
          start = firstDayOfMonth;
          end = lastDayOfMonth;
      }

      // Fetch flat rate entries
      const { data: flatRateData, error: flatRateError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          completed_at,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('entry_type', 'flat_rate')
        .gte('completed_at', start.toISOString())
        .lte('completed_at', end.toISOString());

      if (flatRateError) throw flatRateError;

      // Fetch timeclock entries
      const { data: timeclockData, error: timeclockError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          clock_out,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('entry_type', 'timeclock')
        .gte('clock_out', start.toISOString())
        .lte('clock_out', end.toISOString());

      if (timeclockError) throw timeclockError;

      // Fetch commission entries
      const { data: commissionData, error: commissionError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          completed_at,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('entry_type', 'commission')
        .gte('completed_at', start.toISOString())
        .lte('completed_at', end.toISOString());

      if (commissionError) throw commissionError;

      // Fetch bonus entries
      const { data: bonusData, error: bonusError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          bonus_earnings,
          total_earnings,
          created_at,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('entry_type', 'bonus')
        .gte('created_at', start.toISOString())
        .lte('created_at', end.toISOString());

      if (bonusError) throw bonusError;

      // Debug logging for bonus data
      console.log('🔍 ProfitLoss: Fetched bonus data:', bonusData);
      console.log('🔍 ProfitLoss: Bonus entries count:', bonusData?.length || 0);
      if (bonusData && bonusData.length > 0) {
        const totalBonuses = bonusData.reduce((sum, entry) => sum + (entry.bonus_earnings || entry.total_earnings || 0), 0);
        console.log('💰 ProfitLoss: Total bonus costs for period:', totalBonuses);
      }

      // Helper function to safely extract resource data
      const extractResource = (resource: any) => {
        if (Array.isArray(resource) && resource.length > 0) {
          return {
            first_name: resource[0].first_name || '',
            last_name: resource[0].last_name || ''
          };
        } else if (resource && typeof resource === 'object') {
          return {
            first_name: resource.first_name || '',
            last_name: resource.last_name || ''
          };
        }
        return {
          first_name: '',
          last_name: ''
        };
      };

      // Combine all four types of payroll data by resource
      const combinedData = [
        ...(flatRateData?.map(entry => ({
          id: entry.id,
          resource_id: entry.resource_id,
          total_earnings: entry.total_earnings,
          completed_at: entry.completed_at,
          resource: extractResource(entry.resource)
        })) || []),
        ...(timeclockData?.map(entry => ({
          id: entry.id,
          resource_id: entry.resource_id,
          total_earnings: entry.total_earnings,
          clock_out: entry.clock_out,
          resource: extractResource(entry.resource)
        })) || []),
        ...(commissionData?.map(entry => ({
          id: entry.id,
          resource_id: entry.resource_id,
          total_earnings: entry.total_earnings,
          completed_at: entry.completed_at,
          resource: extractResource(entry.resource)
        })) || []),
        ...(bonusData?.map(entry => ({
          id: entry.id,
          resource_id: entry.resource_id,
          total_earnings: entry.bonus_earnings || entry.total_earnings,
          created_at: entry.created_at,
          resource: extractResource(entry.resource)
        })) || [])
      ] as PayrollEntry[];

      const resourceTotals = combinedData.reduce((acc, entry) => {
        const resourceId = entry.resource_id;
        if (!acc[resourceId]) {
          acc[resourceId] = {
            resource_id: resourceId,
            first_name: entry.resource.first_name,
            last_name: entry.resource.last_name,
            total_earnings: 0
          };
        }
        acc[resourceId].total_earnings += entry.total_earnings || 0;
        return acc;
      }, {} as Record<string, ResourceTotal>);

      setLaborData(Object.values(resourceTotals));
    } catch (error) {
      console.error('Error fetching labor data:', error);
    } finally {
      setIsLoadingLabor(false);
    }
  };

  useEffect(() => {
    const fetchSalesData = async () => {
      try {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        if (!selectedBusiness?.id) {
          return; // Exit if no business is selected
        }

        // Get the date range for comparison
        const now = new Date();
        const currentPeriodStart = new Date();
        const previousPeriodStart = new Date();
        
        // Set up date ranges based on selected period
        switch (selectedDateRange.id) {
          case 'this-month':
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
            previousPeriodStart.setDate(1);
            break;
          case 'last-month':
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() - 1);
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
            previousPeriodStart.setDate(1);
            break;
          case 'next-month':
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() + 1);
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() + 1);
            previousPeriodStart.setDate(1);
            break;
          case 'custom':
            if (customStartDate && customEndDate) {
              currentPeriodStart.setTime(new Date(customStartDate).getTime());
              // For custom range, calculate previous period of same length
              const daysDiff = Math.ceil((new Date(customEndDate).getTime() - new Date(customStartDate).getTime()) / (1000 * 60 * 60 * 24));
              previousPeriodStart.setTime(new Date(customStartDate).getTime());
              previousPeriodStart.setDate(previousPeriodStart.getDate() - daysDiff);
            }
            break;
        }

        // Fetch all bookings with end dates and include discounts and services with category details
        const { data: bookings, error } = await supabase
          .from('bookings')
          .select(`
            *,
            customer:customers!bookings_customer_id_fkey(
              id,
              created_at
            ),
            discounts:booking_discounts(*),
            services:booking_services_with_details(
              *,
              service_category_name
            )
          `)
          .eq('business_id', selectedBusiness.id)
          .eq('is_deleted', false)
          .order('end_date', { ascending: false });

        if (error) throw error;

        // Filter bookings for current period based on end_date and status
        const currentPeriodBookings = (bookings as Booking[]).filter(booking => 
          isDateInRange(booking.end_date, selectedDateRange.id, customStartDate, customEndDate) &&
          booking.status === 'completed'
        );

        // Filter bookings for previous period based on end_date and status
        const previousPeriodBookings = (bookings as Booking[]).filter(booking => {
          if (!booking.end_date || booking.status !== 'completed') return false;
          const bookingDate = new Date(booking.end_date + 'T00:00:00Z');
          return bookingDate >= previousPeriodStart && bookingDate < currentPeriodStart;
        });

        // Standalone (booking-less) invoices count as revenue too — attributed
        // to their issue date, same period rules as bookings.
        const standaloneAll = await fetchStandaloneInvoiceSales(selectedBusiness.id);
        const invoiceDay = (inv: StandaloneInvoiceSale) =>
          new Date(inv.finalizedAt).toLocaleDateString('en-CA'); // yyyy-MM-dd local
        const currentStandalone = standaloneAll.filter((inv) =>
          isDateInRange(invoiceDay(inv), selectedDateRange.id, customStartDate, customEndDate),
        );
        const previousStandalone = standaloneAll.filter((inv) => {
          const d = new Date(invoiceDay(inv) + 'T00:00:00Z');
          return d >= previousPeriodStart && d < currentPeriodStart;
        });

        // Calculate metrics for current period
        const currentTotalSales =
          currentPeriodBookings.reduce((sum, booking) => sum + booking.subtotal, 0) +
          currentStandalone.reduce((sum, inv) => sum + inv.subtotal, 0);
        const currentTotalAfterDiscounts =
          currentPeriodBookings.reduce((sum, booking) => {
            const subtotal = booking.subtotal;
            const discountAmount = booking.discount_amount || 0;
            return sum + (subtotal - discountAmount);
          }, 0) + sumStandaloneInvoiceSales(currentStandalone);

        // Calculate metrics for previous period
        const previousTotalSales =
          previousPeriodBookings.reduce((sum, booking) => sum + booking.subtotal, 0) +
          previousStandalone.reduce((sum, inv) => sum + inv.subtotal, 0);
        const previousTotalAfterDiscounts =
          previousPeriodBookings.reduce((sum, booking) => {
            const subtotal = booking.subtotal;
            const discountAmount = booking.discount_amount || 0;
            return sum + (subtotal - discountAmount);
          }, 0) + sumStandaloneInvoiceSales(previousStandalone);

        // Calculate percentage changes
        const salesChange = previousTotalSales === 0 
          ? 100 
          : ((currentTotalSales - previousTotalSales) / previousTotalSales) * 100;

        const afterDiscountsChange = previousTotalAfterDiscounts === 0
          ? 100
          : ((currentTotalAfterDiscounts - previousTotalAfterDiscounts) / previousTotalAfterDiscounts) * 100;

        const currentOrders = currentPeriodBookings.length + currentStandalone.length;
        const previousOrders = previousPeriodBookings.length + previousStandalone.length;
        const orderChange = previousOrders === 0
          ? 100
          : ((currentOrders - previousOrders) / previousOrders) * 100;

        // Update sales data
        setSalesData({
          totalSales: currentTotalSales,
          salesChange: Number(salesChange.toFixed(1)),
          totalAfterDiscounts: currentTotalAfterDiscounts,
          afterDiscountsChange: Number(afterDiscountsChange.toFixed(1)),
          totalOrders: currentOrders,
          orderChange: Number(orderChange.toFixed(1)),
          bookings: currentPeriodBookings
        });

      } catch (error) {
        console.error('Error fetching sales data:', error);
      }
    };

    fetchSalesData();
  }, [selectedBusiness?.id, selectedDateRange, customStartDate, customEndDate]);

  useEffect(() => {
    const fetchPaymentData = async () => {
      try {
        if (!supabase || !selectedBusiness?.id) return;

        // Get the date range for comparison
        const now = new Date();
        const currentPeriodStart = new Date();
        const previousPeriodStart = new Date();
        
        // Set up date ranges based on selected period
        switch (selectedDateRange.id) {
          case 'this-month':
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
            previousPeriodStart.setDate(1);
            break;
          case 'last-month':
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() - 1);
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
            previousPeriodStart.setDate(1);
            break;
          case 'next-month':
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() + 1);
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() + 1);
            previousPeriodStart.setDate(1);
            break;
          case 'custom':
            if (customStartDate && customEndDate) {
              currentPeriodStart.setTime(new Date(customStartDate).getTime());
              // For custom range, calculate previous period of same length
              const daysDiff = Math.ceil((new Date(customEndDate).getTime() - new Date(customStartDate).getTime()) / (1000 * 60 * 60 * 24));
              previousPeriodStart.setTime(new Date(customStartDate).getTime());
              previousPeriodStart.setDate(previousPeriodStart.getDate() - daysDiff);
            }
            break;
        }

        // Update payment data to match sales data
        setPaymentData({
          totalPaymentsIn: salesData.totalAfterDiscounts,
          totalPaymentsOut: laborData.reduce((sum, resource) => sum + resource.total_earnings, 0),
          paymentsInChange: salesData.afterDiscountsChange,
          paymentsOutChange: 0 // We'll calculate this later when we have historical data
        });

      } catch (error) {
        console.error('Error updating payment data:', error);
      }
    };

    fetchPaymentData();
  }, [selectedBusiness?.id, selectedDateRange, customStartDate, customEndDate, salesData, laborData]);

  useEffect(() => {
    const fetchStockData = async () => {
      try {
        if (!supabase || !selectedBusiness?.id) return;

        // Get the date range for comparison
        const now = new Date();
        const currentPeriodStart = new Date();
        const previousPeriodStart = new Date();
        
        // Set up date ranges based on selected period
        switch (selectedDateRange.id) {
          case 'this-month':
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
            previousPeriodStart.setDate(1);
            break;
          case 'last-month':
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() - 1);
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
            previousPeriodStart.setDate(1);
            break;
          case 'next-month':
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() + 1);
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() + 1);
            previousPeriodStart.setDate(1);
            break;
          case 'custom':
            if (customStartDate && customEndDate) {
              currentPeriodStart.setTime(new Date(customStartDate).getTime());
              const daysDiff = Math.ceil((new Date(customEndDate).getTime() - new Date(customStartDate).getTime()) / (1000 * 60 * 60 * 24));
              previousPeriodStart.setTime(new Date(customStartDate).getTime());
              previousPeriodStart.setDate(previousPeriodStart.getDate() - daysDiff);
            }
            break;
        }

        // Fetch stock history for received items
        const { data: stockHistory, error } = await supabase
          .from('stock_history_with_details')
          .select('*')
          .eq('transaction_type', 'Received')
          .gte('created_at', currentPeriodStart.toISOString())
          .lte('created_at', selectedDateRange.id === 'custom' && customEndDate 
            ? new Date(customEndDate + 'T23:59:59.999Z').toISOString()
            : new Date(currentPeriodStart.getFullYear(), currentPeriodStart.getMonth() + 1, 0, 23, 59, 59, 999).toISOString()
          );

        if (error) {
          console.error('Error fetching stock data:', error);
          throw error;
        }

        console.log('Raw stock history:', stockHistory);

        // Calculate total cost for current period
        const currentTotalCost = (stockHistory || []).reduce((sum, item) => {
          const quantity = Math.abs(Number(item.quantity_change) || 0);
          const price = Number(item.price) || 0;
          const itemTotal = quantity * price;
          
          console.log('Item calculation:', { 
            name: item.item_name,
            category: item.category_name,
            quantity,
            price,
            itemTotal,
            date: item.created_at
          });
          
          return sum + itemTotal;
        }, 0);

        console.log('Current period total cost:', currentTotalCost);

        // Calculate previous period costs
        const { data: previousStockHistory, error: previousError } = await supabase
          .from('stock_history_with_details')
          .select('*')
          .eq('transaction_type', 'Received')
          .gte('created_at', previousPeriodStart.toISOString())
          .lt('created_at', currentPeriodStart.toISOString());

        if (previousError) {
          console.error('Error fetching previous stock data:', previousError);
          throw previousError;
        }

        const previousTotalCost = (previousStockHistory || []).reduce((sum, item) => {
          const quantity = Math.abs(Number(item.quantity_change) || 0);
          const price = Number(item.price) || 0;
          return sum + (quantity * price);
        }, 0);

        // Calculate percentage change
        const suppliesChange = previousTotalCost === 0 
          ? 100 
          : ((currentTotalCost - previousTotalCost) / previousTotalCost) * 100;

        setStockData({
          totalSuppliesCost: currentTotalCost,
          suppliesChange: Number(suppliesChange.toFixed(1))
        });

      } catch (error) {
        console.error('Error fetching stock data:', error);
      }
    };

    fetchStockData();
  }, [selectedBusiness?.id, selectedDateRange, customStartDate, customEndDate]);

  useEffect(() => {
    const fetchFixedExpenses = async () => {
      try {
        if (!supabase || !selectedBusiness?.id) return;

        // Get the date range for comparison
        const now = new Date();
        const currentPeriodStart = new Date();
        const previousPeriodStart = new Date();
        
        // Set up date ranges based on selected period
        switch (selectedDateRange.id) {
          case 'this-month':
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
            previousPeriodStart.setDate(1);
            break;
          case 'last-month':
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() - 1);
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
            previousPeriodStart.setDate(1);
            break;
          case 'next-month':
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() + 1);
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() + 1);
            previousPeriodStart.setDate(1);
            break;
          case 'custom':
            if (customStartDate && customEndDate) {
              currentPeriodStart.setTime(new Date(customStartDate).getTime());
              const daysDiff = Math.ceil((new Date(customEndDate).getTime() - new Date(customStartDate).getTime()) / (1000 * 60 * 60 * 24));
              previousPeriodStart.setTime(new Date(customStartDate).getTime());
              previousPeriodStart.setDate(previousPeriodStart.getDate() - daysDiff);
            }
            break;
        }

        // Fetch operating costs for current period
        const { data: currentExpenses, error: currentError } = await supabase
          .from('operating_costs')
          .select('*')
          .eq('business_id', selectedBusiness.id);

        if (currentError) {
          console.error('Error fetching current operating costs:', currentError);
          throw currentError;
        }

        console.log('Current operating costs:', currentExpenses);

        // Calculate current period total
        const currentTotal = (currentExpenses || []).reduce((sum, expense) => {
          return sum + (Number(expense.amount) || 0);
        }, 0);

        // For fixed expenses, we don't need to fetch previous period since they're fixed
        // We'll set the change percentage to 0 since they should be constant
        setFixedExpenses({
          totalFixedCost: currentTotal,
          fixedCostChange: 0
        });

      } catch (error) {
        console.error('Error fetching operating costs:', error);
      }
    };

    fetchFixedExpenses();
  }, [selectedBusiness?.id, selectedDateRange, customStartDate, customEndDate]);

  const handleStockHistoryToggle = (newValue: boolean) => {
    setUseStockHistory(newValue);
    localStorage.setItem('useStockHistory', JSON.stringify(newValue));
  };

  // Add new function to fetch profit by job data
  const fetchProfitByJobData = async () => {
    if (!supabase || !selectedBusiness?.id) return;

    try {
      console.log('Fetching profit by job data for business:', selectedBusiness.id);
      
      // Get bookings with their costs
      const { data: bookings, error: bookingsError } = await supabase
        .from('bookings')
        .select(`
          id,
          booking_number,
          title,
          subtotal,
          discount_amount,
          end_date,
          status,
          labour_costs,
          materials:booking_materials (
            quantity,
            cost
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('status', 'completed');

      if (bookingsError) {
        console.error('Error fetching bookings:', bookingsError);
        throw bookingsError;
      }

      // Get bonus costs for all completed bookings
      console.log('🔍 ProfitByJob: Fetching bonus costs for completed bookings...');
      const { data: bonusCosts, error: bonusError } = await supabase
        .from('payroll_page')
        .select('booking_id, bonus_earnings, total_earnings')
        .eq('business_id', selectedBusiness.id)
        .eq('entry_type', 'bonus')
        .in('booking_id', (bookings || []).map(b => b.id));

      if (bonusError) {
        console.error('🚨 ProfitByJob: Error fetching bonus costs:', bonusError);
      }

      // Create a map of booking_id to total bonus costs
      const bonusCostMap = (bonusCosts || []).reduce((acc, entry) => {
        const bookingId = entry.booking_id;
        const bonusAmount = entry.bonus_earnings || entry.total_earnings || 0;
        acc[bookingId] = (acc[bookingId] || 0) + bonusAmount;
        return acc;
      }, {} as Record<string, number>);

      console.log('💰 ProfitByJob: Bonus cost map:', bonusCostMap);

      // Transform the data
      const transformedData = (bookings || []).map(booking => {
        // Calculate material costs
        const materialCosts = (booking.materials || []).reduce((sum, material) => {
          const quantity = material.quantity || 0;
          const cost = material.cost || 0;
          return sum + (quantity * cost);
        }, 0);

        // Get bonus costs for this booking
        const bonusCosts = bonusCostMap[booking.id] || 0;
        const totalLaborCosts = (booking.labour_costs || 0) + bonusCosts;

        console.log('Booking costs:', {
          id: booking.id,
          materialCosts,
          baseLabourCosts: booking.labour_costs || 0,
          bonusCosts,
          totalLaborCosts,
          totalCosts: materialCosts + totalLaborCosts
        });

        return {
          id: booking.id,
          booking_number: booking.booking_number,
          title: booking.title,
          subtotal: booking.subtotal || 0,
          discount_amount: booking.discount_amount || 0,
          material_costs: materialCosts,
          labour_costs: totalLaborCosts, // Now includes bonuses
          completed_at: booking.end_date,
          status: booking.status
        };
      });

      console.log('Final transformed data:', transformedData);
      setProfitByJobData(transformedData);
    } catch (error) {
      console.error('Error fetching profit by job data:', error);
      setProfitByJobData([]);
    }
  };

  // Add useEffect to call the new function
  useEffect(() => {
    fetchProfitByJobData();
  }, [selectedBusiness?.id, selectedDateRange, customStartDate, customEndDate]);

  // Update metrics with real data
  const metrics = [
    {
      id: 'total-revenue',
      name: 'Total Sales After Discounts',
      value: `$${salesData.totalAfterDiscounts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      change: `${salesData.afterDiscountsChange >= 0 ? '+' : ''}${salesData.afterDiscountsChange}%`,
      trend: salesData.afterDiscountsChange >= 0 ? 'up' : 'down',
      icon: DollarSign
    },
    {
      id: 'total-costs',
      name: 'Service Costs',
      value: `$${(materialUsageData.reduce((sum, category) => sum + category.total_cost, 0) + laborData.reduce((sum, resource) => sum + resource.total_earnings, 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} `,
      valueExtra: `materials $${materialUsageData.reduce((sum, category) => sum + category.total_cost, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\nlabor $${laborData.reduce((sum, resource) => sum + resource.total_earnings, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      change: '+0.0%',
      trend: 'up',
      icon: Wallet
    },
    {
      id: 'net-profit',
      name: 'Gross Profit',
      value: `$${(salesData.totalAfterDiscounts - (materialUsageData.reduce((sum, category) => sum + category.total_cost, 0) + laborData.reduce((sum, resource) => sum + resource.total_earnings, 0))).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} `,
      valueExtra: `margin ${((salesData.totalAfterDiscounts - (materialUsageData.reduce((sum, category) => sum + category.total_cost, 0) + laborData.reduce((sum, resource) => sum + resource.total_earnings, 0))) / (salesData.totalAfterDiscounts || 1) * 100).toFixed(1)}%`,
      change: '+0.0%',
      trend: 'up',
      icon: TrendingUp
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50/50 via-white to-purple-50/50"
         style={{
           touchAction: 'manipulation',
           WebkitTouchCallout: 'none',
           WebkitUserSelect: 'none',
           paddingTop: 'env(safe-area-inset-top)',
           paddingBottom: 'env(safe-area-inset-bottom)',
           paddingLeft: 'env(safe-area-inset-left)',
           paddingRight: 'env(safe-area-inset-right)',
           fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
         }}>
      {/* Header with Mobile-Optimized Navigation */}
      <div className="backdrop-blur-xl bg-white/70 border-b border-white/20 shadow-sm"
           style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
        <div className="px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            {/* Page Title */}
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Sales Reports</h1>
            </div>
            
            {/* Controls */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              {/* Page Selector */}
              <PortalDropdown
                isOpen={isDropdownOpen}
                onClose={() => setIsDropdownOpen(false)}
                align="right"
                trigger={
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full sm:w-auto backdrop-blur-sm bg-white/60 border border-white/30 rounded-xl px-4 py-3 
                              text-sm font-medium text-gray-700 hover:bg-white/80 transition-all duration-200 
                              flex items-center justify-between gap-2 shadow-sm touch-manipulation"
                    style={{ minHeight: '44px', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <span className="text-gray-900 truncate">{getCurrentPageName()}</span>
                    <ChevronDown 
                      className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} 
                    />
                  </button>
                }
              >
                <div className="py-2">
                  {reportPages.map((page) => {
                    const Icon = page.icon;
                    const isActive = page.path === location.pathname;
                    return (
                      <button
                        key={page.id}
                        onClick={() => handlePageSelect(page.path)}
                        className={`w-full flex items-center px-4 py-3 text-sm transition-all duration-200 touch-manipulation ${
                          isActive 
                            ? 'bg-purple-50/50 text-purple-600' 
                            : 'text-gray-700 hover:bg-white/50'
                        }`}
                      >
                        <Icon className={`h-4 w-4 mr-3 ${isActive ? 'text-purple-500' : 'text-gray-400'}`} />
                        {page.name}
                      </button>
                    );
                  })}
                </div>
              </PortalDropdown>

              {/* Date Selector */}
              <PortalDropdown
                isOpen={isDateDropdownOpen}
                onClose={() => {
                  setIsDateDropdownOpen(false);
                  setIsCustomRangeOpen(false);
                }}
                align="right"
                trigger={
                  <button
                    type="button"
                    onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
                    className="w-full sm:w-auto backdrop-blur-sm bg-white/60 border border-white/30 rounded-xl px-4 py-3 
                              text-sm font-medium text-gray-700 hover:bg-white/80 transition-all duration-200 
                              flex items-center justify-between gap-2 shadow-sm touch-manipulation"
                    style={{ minHeight: '44px', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Calendar className="h-4 w-4 flex-shrink-0 text-purple-500" />
                      <span className="text-gray-900 truncate">{getDateRangeText(selectedDateRange.id, customStartDate, customEndDate)}</span>
                    </div>
                    <ChevronDown 
                      className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform duration-200 ${isDateDropdownOpen ? 'rotate-180' : ''}`} 
                    />
                  </button>
                }
                className="w-full sm:w-64"
              >
                <div className="py-2">
                  {dateRanges.map((dateRange) => {
                    const isActive = dateRange.id === selectedDateRange.id;
                    return (
                      <button
                        key={dateRange.id}
                        onClick={() => handleDateSelect(dateRange)}
                        className={`w-full flex items-center px-4 py-3 text-sm transition-all duration-200 touch-manipulation ${
                          isActive 
                            ? 'bg-purple-50/50 text-purple-600' 
                            : 'text-gray-700 hover:bg-white/50'
                        }`}
                      >
                        {dateRange.icon && <dateRange.icon className={`h-4 w-4 mr-3 ${isActive ? 'text-purple-500' : 'text-gray-400'}`} />}
                        {dateRange.name}
                      </button>
                    );
                  })}
                </div>

                {/* Custom Range Selector */}
                {isCustomRangeOpen && (
                  <div className="border-t border-white/20 p-4 bg-white/30"
                       style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                    <div className="space-y-3">
                      <div>
                        <label htmlFor="start-date" className="block text-xs font-medium text-gray-700 mb-1">
                          Start Date
                        </label>
                        <input
                          type="date"
                          id="start-date"
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                          className="block w-full px-3 py-2 text-sm bg-white/70 border border-white/30 rounded-xl 
                                    focus:outline-none focus:ring-2 focus:ring-purple-500/20 backdrop-blur-sm touch-manipulation"
                          style={{ minHeight: '36px', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                        />
                      </div>
                      <div>
                        <label htmlFor="end-date" className="block text-xs font-medium text-gray-700 mb-1">
                          End Date
                        </label>
                        <input
                          type="date"
                          id="end-date"
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e.target.value)}
                          className="block w-full px-3 py-2 text-sm bg-white/70 border border-white/30 rounded-xl 
                                    focus:outline-none focus:ring-2 focus:ring-purple-500/20 backdrop-blur-sm touch-manipulation"
                          style={{ minHeight: '36px', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                        />
                      </div>
                      <button
                        onClick={handleCustomRangeSubmit}
                        className="w-full px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-xl 
                                 hover:bg-purple-700 transition-colors duration-200 shadow-sm touch-manipulation"
                        style={{ minHeight: '36px' }}
                      >
                        Apply Range
                      </button>
                    </div>
                  </div>
                )}
              </PortalDropdown>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-3 sm:p-6 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
          {/* Metrics Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            {metrics.map((metric) => {
              const Icon = metric.icon;
              return (
                <div
                  key={metric.id}
                  className="backdrop-blur-xl bg-white/70 border border-white/20 rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 sm:p-3 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl shadow-lg">
                        <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                      </div>
                      <span className="text-xs sm:text-sm font-medium text-gray-600 line-clamp-2">{metric.name}</span>
                    </div>
                    <div className={`flex items-center ${metric.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                      {metric.trend === 'up' ? (
                        <ArrowUpRight className="h-3 w-3 sm:h-4 sm:w-4" />
                      ) : (
                        <ArrowDownRight className="h-3 w-3 sm:h-4 sm:w-4" />
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 line-clamp-1">
                      {metric.value}
                    </div>
                    {metric.valueExtra && (
                      <div className="text-xs text-gray-500 line-clamp-2">
                        {metric.valueExtra}
                      </div>
                    )}
                    <div className="flex items-center text-xs">
                      <span className={metric.trend === 'up' ? 'text-green-600' : 'text-red-600'}>
                        {metric.change}
                      </span>
                      <span className="text-gray-500 ml-1">vs last period</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Material Usage and Labor Costs Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
            {/* Material Usage Section */}
            <div className="backdrop-blur-xl bg-white/70 border border-white/20 rounded-2xl p-4 sm:p-6 shadow-lg"
                 style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm sm:text-lg font-bold text-gray-900">Material Usage</h2>
                <span className="text-sm font-medium text-gray-900">
                  ${materialUsageData.reduce((sum, category) => sum + category.total_cost, 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
              </div>

              <div className="space-y-3 max-h-[300px] sm:max-h-[400px] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                {isLoadingMaterials ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-t-2 border-gray-200 rounded-full animate-spin border-t-purple-600"></div>
                  </div>
                ) : materialUsageData.length === 0 ? (
                  <div className="py-8 text-center text-sm text-gray-500">
                    No material usage data available
                  </div>
                ) : (
                  <div className="space-y-3">
                    {materialUsageData.map((category) => (
                      <div key={category.category} className="border border-white/30 rounded-xl overflow-hidden backdrop-blur-sm bg-white/50">
                        <button
                          onClick={() => {
                            setMaterialUsageData(prev =>
                              prev.map(cat =>
                                cat.category === category.category
                                  ? { ...cat, isExpanded: !cat.isExpanded }
                                  : cat
                              )
                            );
                          }}
                          className="w-full flex items-center justify-between p-3 hover:bg-white/70 transition-colors duration-200"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="bg-white/70 rounded-lg p-1.5 border border-white/40">
                              <ChevronRight
                                className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                                  category.isExpanded ? 'transform rotate-90' : ''
                                }`}
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-gray-900 truncate">{category.category}</p>
                              <p className="text-xs text-gray-500">
                                {category.materials.length} {category.materials.length === 1 ? 'material' : 'materials'}
                              </p>
                            </div>
                          </div>
                          <div className="text-right ml-2">
                            <p className="text-[11px] sm:text-sm font-semibold text-gray-900">
                              ${category.total_cost.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </p>
                          </div>
                        </button>

                        {category.isExpanded && (
                          <div className="border-t border-gray-100 bg-gray-50/50">
                            {category.materials.map((material) => (
                              <div
                                key={material.id}
                                className="flex items-center justify-between p-2 sm:p-3 border-b border-gray-100 last:border-b-0 hover:bg-white transition-colors duration-150"
                              >
                                <div className="min-w-0 flex-1">
                                  <p className="text-[11px] sm:text-sm text-gray-700 truncate">{material.name}</p>
                                  <p className="text-[9px] sm:text-xs text-gray-500">
                                    {material.quantity} {material.unit} @ ${material.cost_per_unit.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/{material.unit}
                                  </p>
                                </div>
                                <div className="text-right ml-2">
                                  <p className="text-[11px] sm:text-sm font-medium text-gray-700">
                                    ${material.total_cost.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Labor Report Section */}
            <div className="backdrop-blur-xl bg-white/70 border border-white/20 rounded-2xl shadow-lg overflow-hidden"
                 style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm sm:text-lg font-bold text-gray-900">Labor Report</h2>
                  <div className="text-sm font-medium text-gray-900">
                    ${laborData.reduce((sum, resource) => sum + resource.total_earnings, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-white/50 sticky top-0 z-10" style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                      <tr>
                        <th scope="col" className="px-3 py-2 sm:px-4 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          Resource
                        </th>
                        <th scope="col" className="px-3 py-2 sm:px-4 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          Earnings
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white/30 divide-y divide-white/20" style={{ backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)' }}>
                      {laborData.map((resource) => (
                        <tr key={resource.resource_id} className="hover:bg-white/40 transition-colors duration-200">
                          <td className="px-3 py-2 sm:px-4 sm:py-3 text-xs sm:text-sm text-gray-900 whitespace-nowrap">
                            {resource.first_name} {resource.last_name}
                          </td>
                          <td className="px-3 py-2 sm:px-4 sm:py-3 text-xs sm:text-sm text-gray-900 whitespace-nowrap text-right font-medium">
                            ${resource.total_earnings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Money In and Out - Full Width */}
          <div className="backdrop-blur-xl bg-white/70 border border-white/20 rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300"
               style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 sm:p-3 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl shadow-lg">
                  <BanknoteIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Money In and Out</h2>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600 font-medium">Material Usage</span>
                  <button
                    type="button"
                    onClick={() => handleStockHistoryToggle(!useStockHistory)}
                    className={`
                      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                      transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500/20
                      ${useStockHistory ? 'bg-purple-600' : 'bg-gray-200'}
                    `}
                  >
                    <span
                      className={`
                        pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 
                        transition duration-200 ease-in-out
                        ${useStockHistory ? 'translate-x-5' : 'translate-x-0'}
                      `}
                    />
                  </button>
                  <span className="text-sm text-gray-600 font-medium">Stock History</span>
                </div>
                <div className={`flex items-center ${paymentData.paymentsInChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {paymentData.paymentsInChange >= 0 ? (
                    <ArrowUpCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                  ) : (
                    <ArrowDownCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                  )}
                  <span className="text-xs sm:text-sm font-medium ml-1">
                    {paymentData.paymentsInChange >= 0 ? '+' : ''}{paymentData.paymentsInChange}%
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {/* Net Profit */}
              <div className="p-4 sm:p-5 bg-gray-50/80 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center">
                  <div className="space-y-1">
                    <h3 className="text-sm sm:text-base font-medium text-gray-700">Net Profit</h3>
                    <div className="flex items-baseline gap-2">
                      <span className={`text-xl sm:text-2xl md:text-3xl font-bold ${
                        paymentData.totalPaymentsIn - (
                          laborData.reduce((sum, resource) => sum + resource.total_earnings, 0) + 
                          (useStockHistory ? stockData.totalSuppliesCost : materialUsageData.reduce((sum, category) => sum + category.total_cost, 0)) + 
                          fixedExpenses.totalFixedCost
                        ) >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        ${Math.abs(paymentData.totalPaymentsIn - (
                          laborData.reduce((sum, resource) => sum + resource.total_earnings, 0) + 
                          (useStockHistory ? stockData.totalSuppliesCost : materialUsageData.reduce((sum, category) => sum + category.total_cost, 0)) + 
                          fixedExpenses.totalFixedCost
                        )).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-sm sm:text-base text-gray-500 font-medium">
                        {((paymentData.totalPaymentsIn - (
                          laborData.reduce((sum, resource) => sum + resource.total_earnings, 0) + 
                          (useStockHistory ? stockData.totalSuppliesCost : materialUsageData.reduce((sum, category) => sum + category.total_cost, 0)) + 
                          fixedExpenses.totalFixedCost
                        )) / (paymentData.totalPaymentsIn || 1) * 100).toFixed(1)}% margin
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Money Flow Breakdown */}
              <div className="space-y-5">
                {/* Money In */}
                <div className="space-y-2.5">
                  <div className="flex justify-between items-baseline">
                    <span className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
                      ${paymentData.totalPaymentsIn.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <span className="text-sm sm:text-base text-gray-600 font-medium">Money In</span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden shadow-inner">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full shadow-sm transform origin-left transition-transform duration-1000 ease-out"
                      style={{ width: `${(paymentData.totalPaymentsIn / (paymentData.totalPaymentsIn || 1)) * 100}%` }}
                    ></div>
                  </div>
                </div>

                <div className="space-y-5 pt-2">
                  {/* Labor Costs */}
                  <div className="space-y-2.5">
                    <div className="flex justify-between items-baseline">
                      <span className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
                        ${laborData.reduce((sum, resource) => sum + resource.total_earnings, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-sm sm:text-base text-gray-600 font-medium">Labor Costs</span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden shadow-inner">
                      <div 
                        className="h-full bg-gradient-to-r from-red-500 to-red-400 rounded-full shadow-sm transform origin-left transition-transform duration-1000 ease-out"
                        style={{ width: `${(laborData.reduce((sum, resource) => sum + resource.total_earnings, 0) / (paymentData.totalPaymentsIn || 1)) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Supplies Costs */}
                  <div className="space-y-2.5">
                    <div className="flex justify-between items-baseline">
                      <span className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
                        ${(useStockHistory ? stockData.totalSuppliesCost : materialUsageData.reduce((sum, category) => sum + category.total_cost, 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-sm sm:text-base text-gray-600 font-medium">
                        Supplies Costs {useStockHistory ? '(Stock)' : '(Materials)'}
                      </span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden shadow-inner">
                      <div 
                        className="h-full bg-gradient-to-r from-red-500 to-red-400 rounded-full shadow-sm transform origin-left transition-transform duration-1000 ease-out"
                        style={{ width: `${((useStockHistory ? stockData.totalSuppliesCost : materialUsageData.reduce((sum, category) => sum + category.total_cost, 0)) / (paymentData.totalPaymentsIn || 1)) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Fixed Expenses */}
                  <div className="space-y-2.5">
                    <div className="flex justify-between items-baseline">
                      <span className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
                        ${fixedExpenses.totalFixedCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-sm sm:text-base text-gray-600 font-medium">Fixed Expenses</span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden shadow-inner">
                      <div 
                        className="h-full bg-gradient-to-r from-red-500 to-red-400 rounded-full shadow-sm transform origin-left transition-transform duration-1000 ease-out"
                        style={{ width: `${(fixedExpenses.totalFixedCost / (paymentData.totalPaymentsIn || 1)) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Estimated Profit Report */}
          {/* Profit by Job */}
          <div className="backdrop-blur-xl bg-white/70 border border-white/20 rounded-2xl shadow-lg overflow-hidden"
               style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
            <div className="p-4 sm:p-6">
              <h2 className="text-sm sm:text-lg font-bold text-gray-900 mb-4">Profit by Job</h2>
              <div className="overflow-x-auto">
                <div className="inline-block min-w-full align-middle">
                  <div className="max-h-[400px] overflow-y-auto border-t border-white/20 rounded-lg"
                       style={{ WebkitOverflowScrolling: 'touch' }}>
                    <table className="min-w-full divide-y divide-white/20">
                      <thead className="bg-white/50 sticky top-0 z-10" style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                        <tr>
                          <th scope="col" className="px-3 py-2 sm:px-4 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                            Job #
                          </th>
                          <th scope="col" className="px-3 py-2 sm:px-4 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                            Date
                          </th>
                          <th scope="col" className="px-3 py-2 sm:px-4 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hidden sm:table-cell">
                            Title
                          </th>
                          <th scope="col" className="px-3 py-2 sm:px-4 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                            Revenue
                          </th>
                          <th scope="col" className="px-3 py-2 sm:px-4 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                            Costs
                          </th>
                          <th scope="col" className="px-3 py-2 sm:px-4 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                            Profit
                          </th>
                          <th scope="col" className="px-3 py-2 sm:px-4 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                            Margin
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {profitByJobData
                          .filter((booking) => booking.completed_at && isDateInRange(booking.completed_at, selectedDateRange.id, customStartDate, customEndDate))
                          .sort((a, b) => new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime())
                          .map((booking: ProfitByJobBooking) => {
                            const revenue = booking.subtotal - (booking.discount_amount || 0);
                            const costs = (booking.material_costs || 0) + (booking.labour_costs || 0);
                            const profit = revenue - costs;
                            const margin = revenue > 0 ? (profit / revenue) * 100 : 0;

                            return (
                              <tr key={booking.id} className="hover:bg-white/40 transition-colors duration-200">
                                <td className="px-3 py-2 sm:px-4 sm:py-3 text-[8px] sm:text-xs text-gray-900 whitespace-nowrap">
                                  {booking.booking_number || booking.id}
                                </td>
                                <td className="px-3 py-2 sm:px-4 sm:py-3 text-[8px] sm:text-xs text-gray-500 whitespace-nowrap">
                                  {booking.completed_at ? formatDate(new Date(booking.completed_at)) : ''}
                                </td>
                                <td className="px-3 py-2 sm:px-4 sm:py-3 text-[8px] sm:text-xs text-gray-900 hidden sm:table-cell">
                                  {booking.title}
                                </td>
                                <td className="px-3 py-2 sm:px-4 sm:py-3 text-[8px] sm:text-xs text-gray-900 whitespace-nowrap text-right">
                                  ${revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="px-3 py-2 sm:px-4 sm:py-3 text-[8px] sm:text-xs text-red-600 whitespace-nowrap text-right">
                                  -${costs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className={`px-3 py-2 sm:px-4 sm:py-3 text-[8px] sm:text-xs whitespace-nowrap text-right font-medium ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  ${profit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className={`px-3 py-2 sm:px-4 sm:py-3 text-[8px] sm:text-xs whitespace-nowrap text-right ${margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {margin.toFixed(1)}%
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

